import { RefObject, useEffect } from 'react';

interface UseFollowLogProps {
  ref: RefObject<HTMLElement>;
  anchorRef: RefObject<HTMLElement>;
  enabled: boolean;
  onScroll: () => void;
}

const useFollowLog = ({ ref, anchorRef, enabled, onScroll }: UseFollowLogProps): void => {
  useEffect(() => {
    const container = ref.current;
    const anchor = anchorRef.current;

    if (!enabled || !container || !anchor) {
      return;
    }

    const scrollToAnchor = () => anchor.scrollIntoView({ block: 'end' });
    scrollToAnchor();

    const growth = new MutationObserver(scrollToAnchor);
    growth.observe(container, { subtree: true, childList: true });

    /**
     * Wheeling up means the user took over whatever the anchor's geometry ends up saying: a log
     * chunk arriving in the same frame re-anchors before the observer below samples, and as that
     * one reports state *changes* only, such a departure would never surface. Wheeling down at the
     * end of the log isn't leaving it, so only up counts. Listening on the log rather than the
     * window keeps wheeling over the page's header and summary from cancelling Follow. Touch needs
     * no equivalent — the toolbar holding the Follow toggle is desktop-only.
     */
    const unfollowOnScrollUp = (event: WheelEvent) => {
      if (event.deltaY < 0) {
        onScroll();
      }
    };
    container.addEventListener('wheel', unfollowOnScrollUp, { passive: true });

    /**
     * Catches the departures a wheel event misses: dragging the scrollbar, PageUp, Home. The
     * rootMargin is slack for a slight drag while clicking Follow, which would otherwise read as
     * scrolling away.
     */
    const departure = new IntersectionObserver(
      (entries) => {
        if (!entries[entries.length - 1].isIntersecting) {
          onScroll();
        }
      },
      { rootMargin: '0px 0px 10px 0px' },
    );
    departure.observe(anchor);

    return () => {
      growth.disconnect();
      departure.disconnect();
      container.removeEventListener('wheel', unfollowOnScrollUp);
    };
  }, [enabled, ref, anchorRef, onScroll]);
};

export default useFollowLog;
